import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/categories';

const articleLinks = [
  { name: 'Shoes Spreadsheet Guide', path: '/kakobuy-shoes-spreadsheet' },
  { name: 'Hoodies & Sweaters Guide', path: '/kakobuy-hoodies-spreadsheet' },
  { name: 'T-Shirts Spreadsheet Guide', path: '/kakobuy-tshirts-spreadsheet' },
  { name: 'Jackets Spreadsheet Guide', path: '/kakobuy-jackets-spreadsheet' },
  { name: 'Accessories Spreadsheet Guide', path: '/kakobuy-accessories-spreadsheet' },
  { name: 'Fashion Spreadsheet Guide', path: '/kakobuy-fashion-spreadsheet' },
  { name: 'Shopping Tips & Strategies', path: '/kakobuy-shopping-tips' },
  { name: 'Spreadsheet Comparison Guide', path: '/kakobuy-spreadsheet-comparison' },
  { name: 'Shopping Safety Guide', path: '/kakobuy-safety-guide' },
];

export default function AppNavbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [navScrolled, setNavScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  useEffect(() => {
    function handleScroll() {
      setNavScrolled(window.scrollY > 80);
    }
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    }
    window.addEventListener('scroll', handleScroll, { passive: true });
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setDropdownOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  const isHome = location.pathname === '/';
  const navBg = isHome && !navScrolled
    ? 'bg-transparent'
    : 'bg-background-50/95 backdrop-blur-md border-b border-background-200/60';

  return (
    <>
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${navBg}`}>
        <div className="w-full px-4 md:px-6">
          <div className="flex items-center justify-between h-16 md:h-18">
            <div className="flex items-center gap-8">
              <Link
                to="/"
                className="font-heading text-xl md:text-2xl font-semibold text-foreground-900 tracking-tight whitespace-nowrap cursor-pointer"
              >
                Kakobuy <span className="text-primary-500">Finds</span> Beauty
              </Link>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="hidden md:flex items-center px-4 py-2 text-sm font-medium text-foreground-600 hover:text-foreground-900 transition-colors rounded-lg hover:bg-background-200/60 whitespace-nowrap cursor-pointer"
              >
                Kakobuy Sheet
              </a>
            </div>

            <div className="flex items-center gap-3">
              <div className="relative hidden md:block" ref={dropdownRef}>
                <button
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className="flex items-center gap-2 px-5 py-2.5 bg-secondary-100 hover:bg-secondary-200 text-foreground-700 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer border border-secondary-200/80"
                >
                  <span>Browse Categories</span>
                  <i
                    className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${
                      dropdownOpen ? 'rotate-180' : ''
                    }`}
                  ></i>
                </button>

                {dropdownOpen && (
                  <div className="absolute right-0 mt-2 w-64 bg-background-50 rounded-2xl border border-background-200/80 py-2 max-h-80 overflow-y-auto z-50 shadow-lg">
                    {categories.map((cat) => (
                      <a
                        key={cat.name}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        onClick={() => setDropdownOpen(false)}
                        className="block px-5 py-2.5 text-sm text-foreground-700 hover:bg-secondary-100 hover:text-foreground-900 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        {cat.name}
                      </a>
                    ))}
                  </div>
                )}
              </div>

              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="hidden md:inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-full text-sm font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                Buy It Now
                <i className="ri-arrow-right-line text-base"></i>
              </a>

              <button
                onClick={() => setMobileMenuOpen(true)}
                className="md:hidden flex items-center justify-center w-10 h-10 rounded-xl bg-secondary-100 hover:bg-secondary-200 text-foreground-700 transition-colors cursor-pointer"
                aria-label="Open menu"
              >
                <i className="ri-menu-line text-xl"></i>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile slide-out menu overlay */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-[60] md:hidden">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setMobileMenuOpen(false)}
          ></div>
          <div className="absolute right-0 top-0 bottom-0 w-[85vw] max-w-sm bg-background-50 shadow-2xl overflow-y-auto animate-slide-in">
            <div className="flex items-center justify-between p-4 border-b border-background-200/60">
              <Link
                to="/"
                onClick={() => setMobileMenuOpen(false)}
                className="font-heading text-lg font-semibold text-foreground-900 whitespace-nowrap cursor-pointer"
              >
                Kakobuy <span className="text-primary-500">Finds</span> Beauty
              </Link>
              <button
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center justify-center w-9 h-9 rounded-lg bg-secondary-100 hover:bg-secondary-200 text-foreground-700 transition-colors cursor-pointer"
                aria-label="Close menu"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>

            <div className="p-4 space-y-4">
              <div className="flex flex-col gap-2">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-xl text-sm font-semibold transition-all duration-200 cursor-pointer"
                >
                  <i className="ri-external-link-line text-base"></i>
                  Kakobuy Sheet
                </a>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 bg-secondary-100 hover:bg-secondary-200 text-foreground-700 rounded-xl text-sm font-medium transition-all duration-200 cursor-pointer"
                >
                  <i className="ri-shopping-bag-line text-base"></i>
                  Buy It Now
                </a>
              </div>

              <div>
                <h3 className="text-xs font-semibold text-foreground-400 uppercase tracking-wider px-4 mb-2">
                  Product Categories
                </h3>
                <div className="space-y-0.5">
                  {categories.map((cat, idx) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm text-foreground-700 hover:bg-secondary-100 hover:text-foreground-900 transition-colors cursor-pointer"
                    >
                      <span className="w-7 h-7 rounded-lg bg-secondary-100 flex items-center justify-center flex-shrink-0">
                        <i className={`${getMobileCatIcon(idx)} text-xs text-foreground-500`}></i>
                      </span>
                      {cat.name}
                    </a>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="text-xs font-semibold text-foreground-400 uppercase tracking-wider px-4 mb-2">
                  SEO Guides &amp; Articles
                </h3>
                <div className="space-y-0.5">
                  <Link
                    to="/kakobuy-spreadsheet-guide"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm text-foreground-700 hover:bg-secondary-100 hover:text-foreground-900 transition-colors cursor-pointer"
                  >
                    <span className="w-7 h-7 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                      <i className="ri-article-line text-xs text-accent-600"></i>
                    </span>
                    Complete Beginner Guide
                  </Link>
                  {articleLinks.map((article) => (
                    <Link
                      key={article.path}
                      to={article.path}
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm text-foreground-700 hover:bg-secondary-100 hover:text-foreground-900 transition-colors cursor-pointer"
                    >
                      <span className="w-7 h-7 rounded-lg bg-secondary-100 flex items-center justify-center flex-shrink-0">
                        <i className="ri-file-text-line text-xs text-foreground-500"></i>
                      </span>
                      {article.name}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function getMobileCatIcon(idx: number): string {
  const icons = [
    'ri-shopping-bag-line',
    'ri-t-shirt-line',
    'ri-t-shirt-2-line',
    'ri-shirt-line',
    'ri-user-line',
    'ri-user-smile-line',
    'ri-stack-line',
    'ri-user-3-line',
    'ri-trophy-line',
    'ri-vip-diamond-line',
    'ri-handbag-line',
    'ri-link',
    'ri-drop-line',
    'ri-more-line',
  ];
  return icons[idx] || 'ri-store-line';
}